import { Moment } from "moment";
import { MIN_CARD_HEIGHT_PX } from "@miplatform/constants";
import { getDate, setTime } from "@miplatform/utils";
import {
  IDisabledSlot,
  PrioritySchedulerColumnEvent,
} from "./PrioritySchedulerBoard.types";
import {
  type TimeWindowRange,
  layoutIntervalFitsAnyTemplateWindow,
} from "./autoAssignWindow.utils";

const DEFAULT_STACK_GAP_PX = 0;

export interface PrioritySchedulerEventLayout {
  top: number;
  height: number;
}

export interface DisabledSlotRect {
  top: number;
  bottom: number;
}

export interface UseEventLayoutsParams {
  events: PrioritySchedulerColumnEvent[];
  hourHeight: number;
  columnDate: Moment | undefined;
  dayStartTime?: string;
  dayEndTime?: string;
  stackGapPx?: number;
  templateWindowsById?: Record<string, TimeWindowRange[]>;
  disabledSlots?: IDisabledSlot[];
}

export interface UseEventLayoutsResult {
  layouts: Map<string, PrioritySchedulerEventLayout>;
  maxBottom: number;
}

export interface ComputeEventLayoutsParams extends UseEventLayoutsParams {
  now: Moment;
}

const resolveNonOverlappingTop = (
  desiredTop: number,
  height: number,
  placed: PrioritySchedulerEventLayout[],
  gapPx: number
): number => {
  let top = desiredTop;
  let resolved = false;

  while (!resolved) {
    const bottom = top + height;
    const blocker = findBlocker(placed, top, bottom, gapPx);
    if (!blocker) {
      resolved = true;
    } else {
      top = blocker.top + blocker.height + gapPx;
    }
  }

  return top;
};

const findBlocker = (
  placed: PrioritySchedulerEventLayout[],
  top: number,
  bottom: number,
  gapPx: number
): PrioritySchedulerEventLayout | undefined =>
  placed.find((p) => top < p.top + p.height + gapPx && bottom > p.top - gapPx);

/**
 * When a layout top lands on or inside a disabled slot, snap it to the slot bottom.
 * Slots must be sorted by top so adjacent ranges keep pushing past the full block.
 */
export const snapPastDisabledSlots = (
  top: number,
  slots: DisabledSlotRect[]
): number => {
  let next = top;
  for (const slot of slots) {
    if (next >= slot.top && next < slot.bottom) {
      next = slot.bottom;
    }
  }
  return next;
};

/**
 * Converts a time-of-day string ("HH:mm" or "HH:mm:ss") to a pixel offset
 * from midnight at the given hourHeight.
 */
const timeStringToOffsetPx = (time: string, hourHeight: number): number => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return (hours + minutes / 60) * hourHeight;
};

/**
 * Converts a Moment to a pixel offset from a reference start Moment.
 */
export const momentToOffsetPx = (
  m: Moment,
  startMoment: Moment,
  hourHeight: number
): number => {
  const minutes = m.diff(startMoment, "minutes", true);
  return (minutes / 60) * hourHeight;
};

export const buildDisabledSlotRects = (
  disabledSlots: IDisabledSlot[] | undefined,
  startMoment: Moment,
  hourHeight: number
): DisabledSlotRect[] =>
  (disabledSlots ?? [])
    .map((slot) => ({
      top: momentToOffsetPx(slot.start, startMoment, hourHeight),
      bottom: momentToOffsetPx(slot.end, startMoment, hourHeight),
    }))
    .filter((slot) => slot.bottom > slot.top)
    .sort((a, b) => a.top - b.top);

const durationToHeight = (
  durationMinutes: number | undefined,
  hourHeight: number
): number =>
  durationMinutes ? (durationMinutes / 60) * hourHeight : hourHeight;

const eventDurationMinutes = (
  event: PrioritySchedulerColumnEvent
): number | undefined =>
  event.assigneeExpectedDuration ?? event.expectedDuration;

type EventCategory = "A" | "B" | "C" | "staged" | "forcedNext" | "unscheduled";

export const classifyEvent = (
  event: PrioritySchedulerColumnEvent
): EventCategory => {
  if (event.forcedNextOn) return "forcedNext";
  if (event.isStaged) return "staged";
  if (event.startDate && event.endDate) return "A";
  if (event.startDate && !event.endDate) return "B";
  if (!event.startDate && !event.endDate && event.scheduledStartDate)
    return "C";
  return "unscheduled";
};

/**
 * Computes absolute `top` / `height` for each event in a priority-scheduler column.
 *
 * Events are classified into categories:
 *  A) Has startDate + endDate  -> fixed position spanning actual work time
 *  B) Has startDate, no endDate -> top at startDate, height grows to current time
 *  C) No startDate/endDate, has scheduledStartDate -> time-anchored, pushed down
 *     by current-time indicator when scheduledStartDate < now; tops that land
 *     on/inside disabledSlots snap to the slot bottom
 *  Staged (isStaged) -> placed after assigned events at provisional scheduledStartDate;
 *     same disabled-slot snap as Category C
 *  Forced next (forcedNextOn) -> placed after the current-time indicator, or directly
 *     below in-progress Category B events when present; snaps past disabledSlots
 *  Unscheduled -> stacked sequentially in remaining space; snaps past disabledSlots
 *  Window-demoted Category C (calendar has autoAssignWindows only) -> stacked
 *     after dayEndTime when timeline-pushed (collision-resolved) top is at/past
 *     belonging window end (overhang past window end is allowed);
 *     forcedNextOn events are excluded; snaps past disabledSlots
 *
 * Historical/in-progress cards (A/B) keep their actual time spans and are not
 * pushed by disabledSlots. Planned-card paths (C, staged, forced next,
 * unscheduled, window-demoted) all snap tops that land on/inside a disabled slot
 * to that slot's bottom.
 *
 * Cards that intersect the dayEndTime boundary are stacked after the latest
 * in-day event bottom (or at dayEndTime when none exist).
 */
export function computeEventLayouts({
  events,
  hourHeight,
  columnDate,
  dayStartTime,
  dayEndTime,
  stackGapPx = DEFAULT_STACK_GAP_PX,
  templateWindowsById,
  disabledSlots,
  now,
}: Readonly<ComputeEventLayoutsParams>): UseEventLayoutsResult {
  const layouts = new Map<string, PrioritySchedulerEventLayout>();
  const placed: PrioritySchedulerEventLayout[] = [];

  const placeEvent = (id: string, desiredTop: number, height: number) => {
    const top = resolveNonOverlappingTop(
      desiredTop,
      height,
      placed,
      stackGapPx
    );
    const layout: PrioritySchedulerEventLayout = { top, height };
    layouts.set(id, layout);
    placed.push(layout);

    return layout;
  };

  if (!columnDate) {
    let stackCursor = 0;
    events.forEach((event) => {
      const height = durationToHeight(eventDurationMinutes(event), hourHeight);

      let desiredTop = stackCursor;
      if (event.scheduledStartDate) {
        const start = getDate(event.scheduledStartDate);
        const minutesFromMidnight =
          start.hours() * 60 + start.minutes() + start.seconds() / 60;
        desiredTop = Math.max(0, (minutesFromMidnight / 60) * hourHeight);
      }

      const layout = placeEvent(event.id, desiredTop, height);
      stackCursor = Math.max(
        stackCursor,
        layout.top + layout.height + stackGapPx
      );
    });

    let maxBottom = 0;
    placed.forEach(({ top, height }) => {
      maxBottom = Math.max(maxBottom, top + height);
    });
    return { layouts, maxBottom };
  }

  const startMoment = dayStartTime
    ? setTime(columnDate, dayStartTime)
    : getDate(columnDate).startOf("day");

  const nowOffsetPx = momentToOffsetPx(now, startMoment, hourHeight);

  const dayEndOffsetPx = dayEndTime
    ? timeStringToOffsetPx(dayEndTime, hourHeight) -
      timeStringToOffsetPx(dayStartTime ?? "00:00", hourHeight)
    : undefined;

  const hasCalendarTimeWindows =
    templateWindowsById != null && Object.keys(templateWindowsById).length > 0;

  const disabledSlotRects = buildDisabledSlotRects(
    disabledSlots,
    startMoment,
    hourHeight
  );

  /** Snap desired + collision-resolved tops past disabled slots for planned cards. */
  const resolvePlannedTop = (desiredTop: number, height: number): number => {
    let top = snapPastDisabledSlots(desiredTop, disabledSlotRects);
    top = resolveNonOverlappingTop(top, height, placed, stackGapPx);
    return snapPastDisabledSlots(top, disabledSlotRects);
  };

  const placePlannedEvent = (
    id: string,
    desiredTop: number,
    height: number
  ): PrioritySchedulerEventLayout =>
    placeEvent(id, resolvePlannedTop(desiredTop, height), height);

  // Phase 1: classify
  const catA: PrioritySchedulerColumnEvent[] = [];
  const catB: PrioritySchedulerColumnEvent[] = [];
  const catC: PrioritySchedulerColumnEvent[] = [];
  const staged: PrioritySchedulerColumnEvent[] = [];
  const forcedNext: PrioritySchedulerColumnEvent[] = [];
  const unscheduled: PrioritySchedulerColumnEvent[] = [];
  const windowDemoted: PrioritySchedulerColumnEvent[] = [];

  events.forEach((event) => {
    const cat = classifyEvent(event);
    if (cat === "A") catA.push(event);
    else if (cat === "B") catB.push(event);
    else if (cat === "forcedNext") forcedNext.push(event);
    else if (cat === "C") catC.push(event);
    else if (cat === "staged") staged.push(event);
    else unscheduled.push(event);
  });

  // Phase 2: Category A - fixed position from startDate to endDate
  const sortedCatA = [...catA].sort((a, b) => {
    const ta = a.endDate ? getDate(a.endDate).valueOf() : 0;
    const tb = b.endDate ? getDate(b.endDate).valueOf() : 0;
    return ta - tb;
  });

  sortedCatA.forEach((event) => {
    if (!event.startDate || !event.endDate) return;
    const start = getDate(event.startDate);
    const end = getDate(event.endDate);
    const topPx = Math.max(0, momentToOffsetPx(start, startMoment, hourHeight));
    const height = Math.max(
      MIN_CARD_HEIGHT_PX,
      momentToOffsetPx(end, startMoment, hourHeight) - topPx
    );
    placeEvent(event.id, topPx, height);
  });

  // Phase 3: Category B - top at startDate, height grows to now
  catB.forEach((event) => {
    if (!event.startDate) return;
    const start = getDate(event.startDate);
    const topPx = Math.max(0, momentToOffsetPx(start, startMoment, hourHeight));
    const height = Math.max(MIN_CARD_HEIGHT_PX, nowOffsetPx - topPx);
    placeEvent(event.id, topPx, height);
  });

  // Phase 3b: Forced next - after current time or below in-progress Category B events
  let forcedNextCursor = nowOffsetPx;
  catB.forEach((event) => {
    if (!event.startDate || getDate(event.startDate).isAfter(now)) return;
    const layout = layouts.get(event.id);
    if (layout) {
      forcedNextCursor = Math.max(
        forcedNextCursor,
        layout.top + layout.height + stackGapPx
      );
    }
  });

  const sortedForcedNext = [...forcedNext].sort((a, b) => {
    const ta = a.forcedNextOn ? getDate(a.forcedNextOn).valueOf() : 0;
    const tb = b.forcedNextOn ? getDate(b.forcedNextOn).valueOf() : 0;
    return tb - ta;
  });

  sortedForcedNext.forEach((event) => {
    const height = durationToHeight(eventDurationMinutes(event), hourHeight);
    const layout = placePlannedEvent(event.id, forcedNextCursor, height);
    forcedNextCursor = layout.top + layout.height + stackGapPx;
  });

  // Phase 4: Category C - scheduled, pushed by current-time indicator
  const sortedCatC = [...catC].sort((a, b) => {
    const ta = a.scheduledStartDate
      ? getDate(a.scheduledStartDate).valueOf()
      : 0;
    const tb = b.scheduledStartDate
      ? getDate(b.scheduledStartDate).valueOf()
      : 0;
    return ta - tb;
  });

  sortedCatC.forEach((event) => {
    if (!event.scheduledStartDate) return;
    const height = durationToHeight(eventDurationMinutes(event), hourHeight);
    const scheduledStart = getDate(event.scheduledStartDate);
    let desiredTop = Math.max(
      0,
      momentToOffsetPx(scheduledStart, startMoment, hourHeight)
    );

    const isInDayEndWindow = !dayEndOffsetPx || desiredTop < dayEndOffsetPx;
    const wasTimelinePushed = scheduledStart.isBefore(now) && isInDayEndWindow;

    if (wasTimelinePushed) {
      desiredTop = Math.max(desiredTop, nowOffsetPx);
    }

    // Resolve stacking before window-fit check so demotion uses the final top
    // (collision can push later cards past the belonging window end).
    const resolvedTop = resolvePlannedTop(desiredTop, height);

    if (
      wasTimelinePushed &&
      hasCalendarTimeWindows &&
      templateWindowsById &&
      !event.forcedNextOn &&
      !layoutIntervalFitsAnyTemplateWindow(
        event.workOrderTemplateId,
        startMoment.clone().add((resolvedTop / hourHeight) * 60, "minutes"),
        templateWindowsById
      )
    ) {
      windowDemoted.push(event);
      return;
    }

    placeEvent(event.id, resolvedTop, height);
  });

  // Phase 4b: Staged - after assigned events, at provisional scheduledStartDate
  const sortedStaged = [...staged].sort((a, b) => {
    const ta = a.scheduledStartDate
      ? getDate(a.scheduledStartDate).valueOf()
      : 0;
    const tb = b.scheduledStartDate
      ? getDate(b.scheduledStartDate).valueOf()
      : 0;
    return ta - tb;
  });

  sortedStaged.forEach((event) => {
    if (!event.scheduledStartDate) return;
    const height = durationToHeight(eventDurationMinutes(event), hourHeight);
    const scheduledStart = getDate(event.scheduledStartDate);
    let desiredTop = Math.max(
      0,
      momentToOffsetPx(scheduledStart, startMoment, hourHeight)
    );

    const isInTimeWindow = !dayEndOffsetPx || desiredTop < dayEndOffsetPx;

    if (scheduledStart.isBefore(now) && isInTimeWindow) {
      desiredTop = Math.max(desiredTop, nowOffsetPx);
    }

    placePlannedEvent(event.id, desiredTop, height);
  });

  // Phase 5: Unscheduled - stacked below all placed content
  let stackCursor = 0;
  placed.forEach(({ top, height }) => {
    stackCursor = Math.max(stackCursor, top + height + stackGapPx);
  });

  unscheduled.forEach((event) => {
    const height = durationToHeight(eventDurationMinutes(event), hourHeight);
    const layout = placePlannedEvent(event.id, stackCursor, height);
    stackCursor = Math.max(
      stackCursor,
      layout.top + layout.height + stackGapPx
    );
  });

  // Phase 5b: Window-demoted Category C — stacked after calendar day end
  if (windowDemoted.length > 0) {
    let afterDayEndCursor =
      dayEndOffsetPx ??
      placed.reduce(
        (max, { top, height }) => Math.max(max, top + height + stackGapPx),
        0
      );

    windowDemoted.forEach((event) => {
      if (event.forcedNextOn) return;
      const height = durationToHeight(eventDurationMinutes(event), hourHeight);
      const layout = placePlannedEvent(event.id, afterDayEndCursor, height);
      afterDayEndCursor = layout.top + layout.height + stackGapPx;
    });
  }

  // Phase 6: dayEndTime overflow - push cards that intersect the boundary below it.
  // Stack after the latest in-day event bottom when one exists so overflow cards
  // do not overlap events that hang past dayEnd; otherwise start at dayEndOffsetPx.
  if (dayEndOffsetPx !== undefined) {
    const inDayLayouts = [...layouts.values()].filter(
      (layout) => layout.top < dayEndOffsetPx
    );
    let overflowCursor = dayEndOffsetPx;
    if (inDayLayouts.length > 0) {
      const latestBottom = inDayLayouts.reduce(
        (max, { top, height }) => Math.max(max, top + height),
        0
      );
      overflowCursor = Math.max(dayEndOffsetPx, latestBottom + stackGapPx);
    }

    const sortedByTop = [...layouts.entries()].sort(
      ([, a], [, b]) => a.top - b.top
    );

    sortedByTop.forEach(([id, layout]) => {
      const cardTop = layout.top;
      const intersectsEndOfDay = cardTop >= dayEndOffsetPx;

      if (intersectsEndOfDay) {
        const newTop = overflowCursor;
        const updatedLayout = {
          ...layout,
          top: newTop,
          height: MIN_CARD_HEIGHT_PX,
        };
        layouts.set(id, updatedLayout);

        const placedEntry = placed.find(
          (p) => p.top === layout.top && p.height === layout.height
        );
        if (placedEntry) {
          placedEntry.top = newTop;
          placedEntry.height = updatedLayout.height;
        }

        overflowCursor = newTop + updatedLayout.height + stackGapPx;
      }
    });
  }

  let maxBottom = 0;
  placed.forEach(({ top, height }) => {
    maxBottom = Math.max(maxBottom, top + height);
  });

  return { layouts, maxBottom };
}
